import { Input, Select } from 'antd';
import { FC, useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { AsyncTypeahead, Menu, MenuItem } from 'react-bootstrap-typeahead';
import {
  useTranslation,
  WithTranslation,
  withTranslation
} from 'react-i18next';
import Company from '../../../../Model/company';
import Contact from '../../../../Model/models/contactModel';
import Location from '../../../../Model/models/locationModel';
import Order from '../../../../Model/models/orderModel';
import { CompaniesService } from '../../../../services/companiesService';
import { ContactsService } from '../../../../services/contactsService';
import { LocationsService } from '../../../../services/locationsService';

const ContactInfo: FC<{
  contact: Contact;
}> = (props) => {
  const { t } = useTranslation();

  return (
    <table id="contact-table">
      <tbody>
        <tr>
          <th>
            {t('NAME')} {t('SURNAME')}
          </th>
          <td>
            {props.contact.name} {props.contact.surname}
          </td>
        </tr>
        <tr>
          <th>{t('EMAIL')}</th>
          <td>{props.contact.mail}</td>
        </tr>
        <tr>
          <th>{t('PHONE')}</th>
          <td>{props.contact.phone}</td>
        </tr>
      </tbody>
    </table>
  );
};

const ContactSearch: FC<{
  onSearchResult: (contacts: Contact[]) => void;
  onSelectedContact: (
    contact: Contact,
    locations: Location[],
    companies: Company[]
  ) => void;
  contacts: Contact[];
}> = (props) => {
  const { t } = useTranslation();

  const contactsService: ContactsService = new ContactsService();
  const companiesService: CompaniesService = new CompaniesService();
  const locationsService: LocationsService = new LocationsService();

  const [isSearchLoading, setIsSearchLoading] = useState(false);

  const handleSearch = async (query: string) => {
    setIsSearchLoading(true);
    const contacts = await contactsService.getContactsByEmail(query);
    setIsSearchLoading(false);
    props.onSearchResult(contacts);
  };

  const handleSelect = async (selected: Contact[]) => {
    const contact = selected[0];
    if (contact) {
      const locations = await locationsService.getLocationsByEmail(
        contact.mail
      );
      const companies = await companiesService.getCompaniesByContactId(
        contact.id as number
      );
      props.onSelectedContact(contact, locations, companies);
    }
  };

  return (
    <>
      <Form.Label>{t('SEARCH_CONTACT')}</Form.Label>
      <AsyncTypeahead
        id={'search-contact'}
        isLoading={isSearchLoading}
        labelKey={'mail'}
        key={'id'}
        delay={400}
        renderMenu={(results, menuProps) => (
          <Menu {...menuProps}>
            {results.map((result, index) => (
              <MenuItem key={index} option={result} position={index}>
                {`${result.mail} (${result.name} ${result.surname})`}
              </MenuItem>
            ))}
          </Menu>
        )}
        // renderMenuItemChildren={(option) => (`${option.mail} (${option.name} ${option.surname})`)}
        onSearch={handleSearch}
        onChange={handleSelect}
        options={props.contacts}
      />
    </>
  );
};

const CompanySelect: FC<{
  currentCompanyId?: number;
  companies: Company[];
  onCompanySelect: (companyId: number | undefined) => void;
}> = (props) => {
  const { t } = useTranslation();

  const handleCompanySelect = (value: string | number) => {
    if (value) {
      const selectedCompany = props.companies.find((company) => company.id == value);
      props.onCompanySelect(selectedCompany?.id || undefined);
    } else {
      props.onCompanySelect(undefined);
    }
  };

  return (
    <div style={{ marginTop: '1em' }}>
      <Form.Label>{t('COMPANIES_LIST')}</Form.Label>
      <Select
        style={{ width: '100%' }}
        value={props.currentCompanyId || ''}
        onChange={handleCompanySelect}
      >
        {props.companies.map((company) => (
          <Select.Option key={company.id} value={company.id as number}>
            {company.business_name}
          </Select.Option>
        ))}
        <Select.Option value={''}>{t('NO_ORDER_COMPANY')}</Select.Option>
      </Select>
    </div>
  );
};

const LocationSelect: FC<{
  currentLocationId?: number;
  locations: Location[];
  onLocationSelect: (locationId: number | undefined) => void;
}> = (props) => {
  const { t } = useTranslation();

  return (
    <div style={{ marginTop: '1em' }}>
      <Form.Label>{t('LOCATIONS_LIST')}</Form.Label>
      <Select
        style={{ width: '100%' }}
        value={props.currentLocationId}
        onChange={(locationId) => props.onLocationSelect(locationId)}
      >
        {props.locations.map((location) => (
          <Select.Option key={location.id} value={location.id as number}>
            {location.address}, {location.city} - {location.country}
          </Select.Option>
        ))}
        <Select.Option value={''}>{t('ADD_LOCATION')}</Select.Option>
      </Select>
    </div>
  );
};

const NewContactForm: FC<{
  newContact: Contact;
  onNewContactUpdate: (newContact: Contact) => void;
}> = (props) => {
  const { t } = useTranslation();

  return (
    <table id="contact-table">
      <tbody>
        <tr>
          <th>{t('NAME')}</th>
          <td>
            <Input
              defaultValue={props.newContact.name}
              onChange={(e) => {
                props.onNewContactUpdate({
                  ...props.newContact,
                  name: e.target.value
                });
              }}
            />
          </td>
        </tr>
        <tr>
          <th>{t('SURNAME')}</th>
          <td>
            <Input
              defaultValue={props.newContact.surname}
              onChange={(e) => {
                props.onNewContactUpdate({
                  ...props.newContact,
                  surname: e.target.value
                });
              }}
            />
          </td>
        </tr>
        <tr>
          <th>{t('EMAIL')}</th>
          <td>
            <Input
              defaultValue={props.newContact.mail}
              onChange={(e) => {
                props.onNewContactUpdate({
                  ...props.newContact,
                  mail: e.target.value
                });
              }}
            />
          </td>
        </tr>
        <tr>
          <th>{t('PHONE')}</th>
          <td>
            <Input
              defaultValue={props.newContact.phone}
              onChange={(e) => {
                props.onNewContactUpdate({
                  ...props.newContact,
                  phone: e.target.value
                });
              }}
            />
          </td>
        </tr>
      </tbody>
    </table>
  );
};

const NewLocationForm: FC<{
  newLocation: Location;
  onNewLocationUpdate: (newLocation: Location) => void;
}> = (props) => {
  const { t } = useTranslation();

  return (
    <>
      <hr />
      <div style={{ marginTop: '1em' }}>
        <Form.Label>{t('NEW_LOCATION')}</Form.Label>
        <Row>
          <Col sm={6}>
            <Form.Group controlId={'address'}>
              <Form.Label>{t('ADDRESS')}</Form.Label>
              <Form.Control
                required
                type="text"
                name="address"
                defaultValue={props.newLocation?.address}
                onChange={(e) => {
                  props.onNewLocationUpdate({
                    ...props.newLocation,
                    address: e.target.value
                  });
                }}
              />
            </Form.Group>
          </Col>
          <Col sm={3}>
            <Form.Group controlId={'city'}>
              <Form.Label>{t('CITY')}</Form.Label>
              <Form.Control
                required
                type="text"
                name="city"
                defaultValue={props.newLocation?.city}
                onChange={(e) => {
                  props.onNewLocationUpdate({
                    ...props.newLocation,
                    city: e.target.value
                  });
                }}
              />
            </Form.Group>
          </Col>
          <Col sm={3}>
            <Form.Group controlId={'postalCode'}>
              <Form.Label>{t('POSTAL_CODE')}</Form.Label>
              <Form.Control
                required
                type="text"
                name="postalcode"
                defaultValue={props.newLocation?.postcode}
                onChange={(e) => {
                  props.onNewLocationUpdate({
                    ...props.newLocation,
                    postcode: e.target.value
                  });
                }}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col sm={6}>
            <Form.Group controlId={'province'}>
              <Form.Label>{t('PROVINCE')}</Form.Label>
              <Form.Control
                required
                type="text"
                name="province"
                defaultValue={props.newLocation?.province}
                onChange={(e) => {
                  props.onNewLocationUpdate({
                    ...props.newLocation,
                    province: e.target.value
                  });
                }}
              />
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group controlId={'country'}>
              <Form.Label>{t('COUNTRY')}</Form.Label>
              <Form.Control
                required
                type="text"
                name="country"
                defaultValue={props.newLocation?.country}
                onChange={(e) => {
                  props.onNewLocationUpdate({
                    ...props.newLocation,
                    country: e.target.value
                  });
                }}
              />
            </Form.Group>
          </Col>
        </Row>
      </div>
    </>
  );
};

interface OrderFormBillingModalProps extends WithTranslation {
  order: Order;
  location?: Location;
  contact?: Contact;
  onModalClose: () => void;
  onSave: (contact: Contact, location: Location, company_id: number | undefined) => void;
}

const OrderFormBillingModalFC: FC<OrderFormBillingModalProps> = (props) => {
  const companiesService: CompaniesService = new CompaniesService();
  const locationsService: LocationsService = new LocationsService();

  const { t } = useTranslation();

  const [order, setOrder] = useState<Order>(props.order);
  const [location, setLocation] = useState<Location | undefined>(
    props.location
  );
  const [contact, setContact] = useState<Contact | undefined>(props.contact);
  const [showModal, setShowModal] = useState(true);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [newContact, setNewContact] = useState<Contact | undefined>();
  const [locations, setLocations] = useState<Location[]>([]);
  const [newLocation, setNewLocation] = useState<Location | undefined>();
  const [companies, setCompanies] = useState<Company[]>([]);

  const [canSave, setCanSave] = useState(false);

  useEffect(() => {
    async function getLocations() {
      const fetchedLocations = await locationsService.getLocationsByEmail(
        props.contact?.mail as string
      );
      setLocations(fetchedLocations);
    }
    async function getCompanies() {
      const fetchedCompanies = await companiesService.getCompaniesByContactId(
        props.contact?.id as number
      );
      setCompanies(fetchedCompanies);
    }

    if (props.contact) {
      getLocations();
      getCompanies();
    }
  }, [props.contact]);

  useEffect(() => {
    if ((!contact && !newContact) || (!location && !newLocation)) {
      setCanSave(false);
    }
    if (
      newLocation &&
      (!newLocation.address ||
        !newLocation.city ||
        !newLocation.country ||
        !newLocation.postcode ||
        !newLocation.province)
    ) {
      setCanSave(false);
    }
    if (newContact && !newContact.mail) {
      setCanSave(false);
    }
    setCanSave(true);
  }, [contact, newContact, location, newLocation]);

  const handleSave = () => {
    if ((contact || newContact) && (location || newLocation)) {
      const billing = location || newLocation;
      const billingContact = contact || newContact;
      if (billing && billingContact) {
        billing.contact_id = billingContact.id;
        props.onSave(billingContact, billing, order.company_id);
      }
    }
  };

  const handleNewContact = () => {
    const emptyContact = new Contact();
    setContact(undefined);
    setNewContact(emptyContact);
    setLocation(undefined);
    setLocations([]);
    setCompanies([]);
  };

  return (
    <Modal
      show={showModal}
      onHide={() => {
        setShowModal(false);
        props.onModalClose();
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title>{t('BILLING_DATA')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ContactSearch
          contacts={contacts}
          onSearchResult={(resultContacts) => {
            setContacts(resultContacts);
            setLocation(undefined);
            setNewLocation(undefined);
          }}
          onSelectedContact={(selectedContact, contactLocations, contactCompanies) => {
            setContact(selectedContact);
            setLocations(contactLocations);
            setCompanies(contactCompanies);
          }}
        />
        <hr />
        {(contact || newContact) && (
          <>
            <div style={{ marginTop: '1em' }}>
              <Form.Label>{t('CONTACT')}</Form.Label>
              {contact && <ContactInfo contact={contact} />}
              {newContact && !contact && (
                <NewContactForm
                  newContact={newContact}
                  onNewContactUpdate={(newUpdatedContact) => setNewContact(newUpdatedContact)}
                />
              )}
            </div>
            <hr />
            <CompanySelect
              currentCompanyId={order.company_id}
              companies={companies}
              onCompanySelect={(companyId) => {
                setOrder((newOrder) => ({ ...newOrder, company_id: companyId }));
              }}
            />
            <hr />
            <LocationSelect
              currentLocationId={location?.id}
              locations={locations}
              onLocationSelect={(locationId) => {
                if (locationId) {
                  const selectedLocation = locations.find(
                    (locationElement) => locationElement.id == locationId
                  );
                  setLocation(selectedLocation);
                  setNewLocation(undefined);
                } else {
                  setLocation(undefined);
                  setNewLocation(new Location());
                }
              }}
            />
            {newLocation && (
              <NewLocationForm
                newLocation={newLocation}
                onNewLocationUpdate={(newUpdatedLocation) =>
                  setNewLocation(newUpdatedLocation)
                }
              />
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!newContact && (
          <Button
            style={{ marginLeft: '1em', cursor: 'pointer' }}
            onClick={handleNewContact}
          >
            {t('ADD_CONTACT')}
          </Button>
        )}
        <Button disabled={!canSave} onClick={handleSave}>
          {t('SAVE')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default withTranslation()(OrderFormBillingModalFC);
